<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { IconChatActive } from '@/components/design/icons';
import HitlInterruptCard, {
  type HitlSubmitPayload,
} from '@/components/humanInTheLoop/HitlInterruptCard.vue';
import type { NormalizedInterrupt } from '@/utils/humanInTheLoop/normalizeInterrupt';
import type { ChatStatusGroup } from '@/composables/useChatStatusGroups';
import type { GaiaChatMessageItem } from '@/utils/chat/types';
import { stripGaiaLinks } from '@/utils/chat/gaiaLinks';
import GaiaEmptyState from '../../composite/GaiaEmptyState.vue';
import GaiaTag from '../../base/GaiaTag.vue';
import GaiaChatMessage from './GaiaChatMessage.vue';
import GaiaChatStatus from './GaiaChatStatus.vue';

const props = defineProps({
  messages: {
    type: Array as PropType<GaiaChatMessageItem[]>,
    default: () => [],
  },
  /** A run is in flight — only the last assistant message reacts to it. */
  streaming: {
    type: Boolean,
    default: false,
  },
  /**
   * What the run is doing, already trimmed by the host to what is worth
   * showing. Rendered under the live answer — one surface, whether or not any
   * tokens have arrived yet.
   */
  statusGroups: {
    type: Array as PropType<ChatStatusGroup[]>,
    default: () => [],
  },
  userInitials: {
    type: String,
    default: '',
  },
  /** Blocks the interrupt cards while another run is already streaming. */
  busy: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  'hitl-submit': [interrupt: NormalizedInterrupt, payload: HitlSubmitPayload];
  'open-reference': [path: string];
}>();

const lastIndex = computed(() => props.messages.length - 1);

/** Only the final bubble streams — earlier ones are settled history. */
function isLive(index: number): boolean {
  return props.streaming && index === lastIndex.value;
}

function attachmentNames(message: GaiaChatMessageItem): string[] {
  if (message.selectedDocumentNames?.length) return message.selectedDocumentNames;
  const count = message.selectedDocumentCount ?? message.selectedDocumentKeys?.length ?? 0;
  if (!count) return [];
  // The general-chat grammar carries uids, not names — the count is all we can
  // honestly show without a lookup per uid.
  return [`${count} document${count === 1 ? '' : 's'} referenced`];
}
</script>

<template>
  <div style="padding: 4px 0 28px">
    <gaia-empty-state v-if="!props.messages.length" title="New conversation">
      <template #icon>
        <n-icon :component="IconChatActive" :size="18" />
      </template>
      Ask anything below — or reopen an earlier chat from History.
    </gaia-empty-state>

    <n-flex v-else vertical :size="26">
      <gaia-chat-message
        v-for="(message, index) in props.messages"
        :key="message.id"
        :sender="message.role"
        :initials="props.userInitials"
        :streaming="isLive(index) && message.content.length > 0"
        :failed="message.failed"
        markdown
        :content="message.content"
        @open-reference="path => emit('open-reference', path)"
      >
        <!-- Raw while streaming: reference links show as their names, never their uids. -->
        {{ stripGaiaLinks(message.content) }}

        <!-- The run's progress, shown once and only on the live bubble. It
             stays put as tokens arrive rather than being swapped out, so a
             tool that outlives the first token keeps reporting. -->
        <template v-if="isLive(index) && message.role === 'assistant'" #status>
          <gaia-chat-status :groups="props.statusGroups" />
        </template>

        <template v-if="attachmentNames(message).length" #attachments>
          <gaia-tag
            v-for="name in attachmentNames(message)"
            :key="name"
            variant="muted"
            size="small"
          >
            {{ name }}
          </gaia-tag>
        </template>

        <template v-if="message.interrupt" #interrupt>
          <n-flex vertical :size="10">
            <hitl-interrupt-card
              v-for="(interrupt, interruptIndex) in message.interrupt.interrupts"
              :key="interrupt.interruptId"
              :interrupt="interrupt"
              :status="interrupt.status"
              :error-message="interrupt.errorMessage"
              :disabled="props.busy"
              :active="interruptIndex === 0"
              @submit="payload => emit('hitl-submit', interrupt, payload)"
            />
          </n-flex>
        </template>
      </gaia-chat-message>
    </n-flex>
  </div>
</template>
